/**
 * Modal lightbox for browsing gallery items. Extracted so the public Galerie
 * page and the per-formation gallery section share one implementation.
 *
 * Keyboard: ← previous, → next, Esc to close.
 * Body scroll-lock is the caller's responsibility (set `overflow: hidden`
 * on body while the lightbox is open).
 */

import type { GalleryItem } from '@mtc/shared';
import { useCallback, useEffect } from 'react';

type Props = {
  items: GalleryItem[];
  /** Index of the active item; `null` = closed. */
  activeIndex: number | null;
  onChange: (next: number | null) => void;
};

export function GalleryLightbox({ items, activeIndex, onChange }: Props) {
  const close = useCallback(() => onChange(null), [onChange]);
  const next = useCallback(() => {
    if (activeIndex === null || items.length === 0) return;
    onChange((activeIndex + 1) % items.length);
  }, [activeIndex, items.length, onChange]);
  const prev = useCallback(() => {
    if (activeIndex === null || items.length === 0) return;
    onChange((activeIndex - 1 + items.length) % items.length);
  }, [activeIndex, items.length, onChange]);

  useEffect(() => {
    if (activeIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [activeIndex, close, next, prev]);

  if (activeIndex === null || !items[activeIndex]) return null;
  const item = items[activeIndex];
  const counter = `${activeIndex + 1} / ${items.length}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(15,25,22,0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          prev();
        }}
        aria-label="Image précédente"
        style={arrowStyle('left')}
      >
        ‹
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          next();
        }}
        aria-label="Image suivante"
        style={arrowStyle('right')}
      >
        ›
      </button>
      <button
        type="button"
        onClick={close}
        aria-label="Fermer"
        style={{
          position: 'absolute',
          top: 20,
          right: 24,
          width: 40,
          height: 40,
          borderRadius: 999,
          background: 'rgba(255,255,255,0.1)',
          color: 'white',
          border: 'none',
          fontSize: 22,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ×
      </button>
      <div
        style={{
          position: 'absolute',
          top: 24,
          left: 32,
          color: 'rgba(255,255,255,0.7)',
          fontFamily: 'var(--mtc-font-mono)',
          fontSize: 11,
          letterSpacing: '0.15em',
        }}
      >
        {counter}
      </div>
      {item.kind === 'video' ? (
        <video
          src={item.url}
          controls
          autoPlay
          playsInline
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: '92vw',
            maxHeight: '85vh',
            borderRadius: 4,
            boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
            background: 'black',
          }}
        />
      ) : (
        <img
          src={item.url}
          alt={item.caption ?? ''}
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: '92vw',
            maxHeight: '85vh',
            objectFit: 'contain',
            borderRadius: 4,
            boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
          }}
        />
      )}
      {(item.caption || item.eventLabel) && (
        <div
          style={{
            position: 'absolute',
            bottom: 24,
            left: 0,
            right: 0,
            textAlign: 'center',
            color: 'rgba(255,255,255,0.85)',
            fontSize: 14,
            padding: '0 32px',
          }}
        >
          {item.caption}
          {item.eventLabel && (
            <div
              style={{
                fontSize: 11,
                opacity: 0.65,
                marginTop: 4,
                fontFamily: 'var(--mtc-font-mono)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              {item.eventLabel}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function arrowStyle(side: 'left' | 'right'): React.CSSProperties {
  return {
    position: 'absolute',
    top: '50%',
    // Tighter offset so the arrows don't overlap the image on narrow phones
    // (the image is up to 92vw wide, leaving little side margin).
    [side]: 10,
    transform: 'translateY(-50%)',
    width: 44,
    height: 44,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.1)',
    color: 'white',
    border: 'none',
    fontSize: 28,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}
